import React, { Component } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import axios from '../lib/axios';
import Spinner from './Spinner';
import Icon from './Icon';

export default class PaginatedFetchTable extends Component {
  static propTypes = {
    apiUrl: PropTypes.string,
    perPage: PropTypes.number.isRequired,
    emptyImg: PropTypes.string.isRequired,
    defaultSort: PropTypes.string,
    querySort: PropTypes.bool,
    columns: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      className: PropTypes.string,
      sort: PropTypes.shape({
        id: PropTypes.string.isRequired,
        direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
      }),
    })).isRequired,
    renderRow: PropTypes.func.isRequired,
    requestThrottle: PropTypes.number,
    onLoad: PropTypes.func,
  };

  static defaultProps = {
    apiUrl: '',
    querySort: false,
    defaultSort: null,
    requestThrottle: 250,
    onLoad: () => {},
  }

  constructor(props) {
    super(props);

    this.requestCancelToken = null;
    this.request = null;

    this.state = {
      items: [],
      sort: null,
      direction: null,
      itemsCount: 0,
      page: 1,
      hasQuery: false,
      loading: true,
    };
  }

  componentDidMount() {
    this.unlistenParamsChange = router.listen(this.handleParamsChange);
    this.handleLoad();
  }

  componentWillUnmount() {
    this.unlistenParamsChange();
  }

  handleParamsChange = () => {
    this.setState({ loading: true }, this.handleLoad);
  };

  handleLoad = () => {
    if (this.request) {
      this.requestCancelToken.cancel();
    }

    const params = qs.parse(window.location.search.slice(1));
    const { sort, direction } = params;
    const page = params.page ? parseInt(params.page, 10) : 1;

    clearTimeout(this.requestThrottle);

    this.setState({ hasQuery: !!params.query }, () => {
      this.requestThrottle = setTimeout(
        () => this.performRequest(params, sort, direction, page),
        this.props.requestThrottle
      );
    });
  };

  performRequest = (params, sort, direction, page) => {
    this.requestCancelToken = axios.CancelToken.source()

    this.request = axios.get(
      this.props.apiUrl,
      {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets', depth: 2 }),
        cancelToken: this.requestCancelToken.token,
      }
    );

    this.request.then(response => {
      this.setState({
        loading: false,
        sort,
        direction,
        page,
        items: response.data.data,
        itemsCount: response.data.meta.total_count,
      }, () => this.props.onLoad(params, this.state.itemsCount));
    });
  }

  renderHeader() {
    return this.props.columns.map((col) => {
      let content = col.icon ? <Icon tooltip={col.title} name={col.icon} /> : col.title;

      if (((this.state.hasQuery && this.props.querySort) || !this.state.hasQuery) && col.sort) {
        const directionMap = { asc: 'desc', desc: 'asc' };
        const { sort, direction } = this.state;
        const defaultSort = col.sort.id === this.props.defaultSort && !sort;
        const { cssClass, newDirection } = (col.sort.id === sort) ? {
          cssClass: `SortableLink SortableLink--active SortableLink--${direction}`,
          newDirection: directionMap[direction],
        } : {
          cssClass: `SortableLink ${defaultSort ? 'SortableLink--active' : ''} SortableLink--${col.sort.direction}`,
          newDirection: defaultSort ? directionMap[col.sort.direction] : col.sort.direction,
        };

        const handleSort = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const output = { sort: col.sort.id, direction: newDirection };
          const params = Object.assign(qs.parse(window.location.search.slice(1)), output);
          delete params.page;
          const search = qs.stringify(params, { arrayFormat: 'brackets' });
          router.push({ pathname: window.location.pathname, search });
        };

        content = (
          <a href={`?sort=${col.sort.id}&direction=${newDirection}`} onClick={handleSort} className={cssClass}>
            {content}
          </a>
        );
      }

      return <th key={col.title} className={col.className}>{content}</th>;
    });
  }

  renderBody() {
    return this.state.items.map((item) => this.props.renderRow(item));
  }

  renderPagination() {
    return (
      <>
        { (this.state.itemsCount > this.props.perPage)
          && (
            <div className="pagination-centered">
              <ul className="pagination">
                {this.renderPages()}
              </ul>
            </div>
          )}
      </>
    );
  }

  renderPages() {
    const totalPages = Math.ceil(this.state.itemsCount / this.props.perPage);
    let start = Math.max(this.state.page - 4, 1);
    const end = Math.min(this.state.page + 4, totalPages);
    const pages = [];

    if (start > 1) {
      pages.push(this.renderPageNav('« First', 1));
    }
    if (this.state.page > start) {
      pages.push(this.renderPageNav('‹ Prev', this.state.page - 1));
    }
    if (start > 1) {
      pages.push(this.renderPageNav('…'));
    }
    while (start <= end) {
      const isCurrent = start === this.state.page;
      pages.push(this.renderPageNav(start, isCurrent ? null : start, isCurrent));

      start += 1;
    }
    if (end < totalPages) {
      pages.push(<li><span className="u-text-grey">...</span></li>);
    }
    if (this.state.page < end) {
      pages.push(this.renderPageNav('Next ›', this.state.page + 1));
    }
    if (end < totalPages) {
      pages.push(this.renderPageNav('Last »', totalPages));
    }

    return pages;
  }

  renderPageNav(text, page, current) {
    const handlePagination = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (page) {
        const output = { page };
        const params = Object.assign(qs.parse(window.location.search.slice(1)), output);
        const search = qs.stringify(params, { arrayFormat: 'brackets' });
        router.push({ pathname: location.pathname, search });
      }
    };
    return (
      <li className={current ? 'current' : ''}>
        <a href={`?page=${page}` || '#'} onClick={handlePagination}>{text}</a>
      </li>
    );
  }

  renderNoResults() {
    return (
      <div className="u-flex u-flex-col u-items-center u-justify-center u-py-6 u-h-48">
        <img src={this.props.emptyImg} alt="no data" />
        <div className="u-text-grey-light u-italic u-text-sm u-mt-2 u-text-center">
          This table will be populated with the data from your community.
          <br />
          There&apos;s not enough information to show yet.
        </div>
      </div>
    );
  }

  renderLoading() {
    return (
      <div
        className="u-fixed u-z-100 u-inset-0 u-flex u-items-center u-justify-center"
        style={{ backgroundColor: 'rgb(0,0,0,0.1)' }}
      >
        <div className="u-text-center u-text-lg u-font-light u-p-8 u-bg-white u-shadow">
          <Spinner />
          Loading…
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="u-panel">
        {this.state.loading && this.renderLoading()}
        <table className="union-table">
          <thead>
            <tr>
              {this.renderHeader()}
            </tr>
          </thead>
          <tbody>
            {this.renderBody()}
          </tbody>
        </table>
        {this.renderPagination()}
        {!this.state.loading && this.state.items.length === 0 && this.renderNoResults()}
      </div>
    );
  }
}
